/**
 * HealthCards — 4 multi-data composite health cards:
 * 1. InvoiceCollectionHealthCard  — paid %, aging buckets, by salesperson
 * 2. DealVelocityCard             — healthy % ring, stalled deals, by stage
 * 3. WinLossIntelligenceCard      — win rate ring, trend, by category/rep
 * 4. ActivityCadenceHealthCard    — on-target %, by person, team weekly bars
 */
import React, { useState, useEffect, useMemo } from 'react';
import { crmAPI } from '../../lib/api';
import { useCurrency } from '../../lib/CurrencyContext';
import { Skeleton } from '../ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { buildHealthCardParams } from './healthCardParams';
import { HealthFilterScopeRibbon } from './HealthFilterScopeRibbon';
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle,
  Users, Activity, BarChart2, Target, DollarSign, FileText, Zap
} from 'lucide-react';

// ── Shared ring chart ─────────────────────────────────────────────────────────
function Ring({ pct, size = 110, sw = 11, color }) {
  const r = (size - sw) / 2;
  const circ = 2 * Math.PI * r;
  const fill = Math.min(pct, 100) / 100 * circ;
  const c = color || (pct >= 80 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#dc2626');
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={sw} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={c} strokeWidth={sw}
        strokeDasharray={`${fill} ${circ - fill}`} strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.9s ease' }} />
    </svg>
  );
}

// ── Shared card shell ─────────────────────────────────────────────────────────
function HealthCardShell({ title, icon: Icon, iconColor, tabs, activeTab, onTabChange, children, loading, testId, scopeFilters }) {
  if (loading) return (
    <div className="h-full rounded-2xl border border-slate-100 bg-white p-4 space-y-3"
      style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      <Skeleton className="h-5 w-48" />
      <Skeleton className="h-28 w-full rounded-xl" />
      <div className="grid grid-cols-3 gap-2">{[0,1,2].map(i => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
    </div>
  );
  return (
    <div className="h-full rounded-2xl border border-slate-100 bg-white flex flex-col overflow-hidden"
      style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }} data-testid={testId}>
      {/* Header */}
      <div className="px-4 pt-4 pb-2 shrink-0 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
            <Icon className="h-4 w-4" style={{ color: iconColor || '#800000' }} />
            {title}
          </h3>
          <div className="flex gap-0.5">
            {tabs.map(t => (
              <button key={t.id} onClick={() => onTabChange(t.id)}
                className={`px-2 py-0.5 text-[10px] font-semibold rounded-lg transition-all ${activeTab === t.id ? 'text-white' : 'text-muted-foreground hover:bg-slate-100'}`}
                style={activeTab === t.id ? { backgroundColor: iconColor || '#800000' } : {}}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <HealthFilterScopeRibbon filters={scopeFilters} />
      </div>
      <div className="flex-1 overflow-auto px-4 pb-4">{children}</div>
    </div>
  );
}

// ── KPI strip ─────────────────────────────────────────────────────────────────
function KpiStrip({ items }) {
  return (
    <div className={`grid gap-2 mb-3 grid-cols-${items.length}`} style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}>
      {items.map((kpi, i) => (
        <div key={i} className="rounded-xl p-2.5 text-center" style={{ background: kpi.bg || '#f8fafc', border: `1px solid ${kpi.border || '#e2e8f0'}` }}>
          <p className="text-[9px] font-bold uppercase tracking-wide" style={{ color: kpi.labelColor || '#64748b' }}>{kpi.label}</p>
          <p className="text-base font-black mt-0.5" style={{ color: kpi.valueColor || '#0f172a' }}>{kpi.value}</p>
          {kpi.sub && <p className="text-[9px] mt-0.5" style={{ color: kpi.subColor || '#94a3b8' }}>{kpi.sub}</p>}
        </div>
      ))}
    </div>
  );
}

// ── Horizontal bar ────────────────────────────────────────────────────────────
function HBar({ items, maxValue, color = '#800000', formatValue }) {
  return (
    <div className="space-y-1.5">
      {items.map((item, i) => {
        const pct = maxValue > 0 ? Math.round((item.value || 0) / maxValue * 100) : 0;
        return (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground truncate w-28 shrink-0">{item.label}</span>
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: color }} />
            </div>
            <span className="text-[10px] font-semibold w-16 text-right shrink-0">{formatValue ? formatValue(item.value) : item.value}</span>
            {item.suffix && <span className="text-[9px] text-muted-foreground shrink-0">{item.suffix}</span>}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CARD 1: Invoice Collection Health
// ═══════════════════════════════════════════════════════════════════════════════
export function InvoiceCollectionHealthCard({ filters = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const { formatCurrency, currency } = useCurrency();
  const navigate = useNavigate();
  const requestParams = buildHealthCardParams(filters);
  const requestParamsKey = useMemo(() => JSON.stringify(requestParams), [requestParams]);

  useEffect(() => {
    setLoading(true);
    crmAPI.getInvoiceCollectionHealth(requestParams)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestParamsKey]);

  const s = data?.summary || {};
  const aging = data?.aging || {};
  const fc = v => formatCurrency(v, currency);

  const AGING = [
    { key: '0_30',   label: '0–30d',  color: '#f59e0b', bg: '#fffbeb', border: '#fcd34d' },
    { key: '31_60',  label: '31–60d', color: '#f97316', bg: '#fff7ed', border: '#fdba74' },
    { key: '61_90',  label: '61–90d', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
    { key: '90_plus',label: '90d+',   color: '#7f1d1d', bg: '#fef2f2', border: '#ef4444' },
  ];
  const totalAging = AGING.reduce((s, b) => s + (aging[b.key]?.amount || 0), 0);

  return (
    <HealthCardShell title="Invoice Collection Health" icon={FileText} iconColor="#059669"
      tabs={[{id:'overview',label:'Overview'},{id:'aging',label:'Aging'},{id:'by-rep',label:'By Rep'}]}
      activeTab={tab} onTabChange={setTab} loading={loading} testId="invoice-collection-health" scopeFilters={filters}>
      
      {tab === 'overview' && data && (
        <>
          {/* Ring + KPIs */}
          <div className="flex items-center gap-4 mb-3">
            <div className="relative shrink-0">
              <Ring pct={s.collection_pct || 0} color="#059669" />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-black text-emerald-700">{s.collection_pct}%</span>
                <span className="text-[9px] text-muted-foreground">collected</span>
              </div>
            </div>
            <div className="flex-1 space-y-1.5">
              <KpiStrip items={[
                { label: 'Invoiced', value: s.total_count, sub: fc(s.total_value), bg: '#f8fafc', border: '#e2e8f0', labelColor: '#64748b', valueColor: '#0f172a' },
                { label: 'Paid', value: s.paid_count, sub: fc(s.paid_value), bg: '#ecfdf5', border: '#bbf7d0', labelColor: '#065f46', valueColor: '#059669' },
                { label: 'Overdue', value: s.outstanding_count, sub: fc(s.outstanding_value), bg: '#fef2f2', border: '#fecaca', labelColor: '#991b1b', valueColor: '#dc2626' },
              ]} />
            </div>
          </div>
          {/* Aging summary bar */}
          <div className="rounded-xl bg-slate-50 p-2.5">
            <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Overdue Distribution — {fc(totalAging)}</p>
            <div className="flex h-6 rounded-lg overflow-hidden gap-0.5">
              {AGING.map(b => {
                const pct = totalAging > 0 ? (aging[b.key]?.amount || 0) / totalAging * 100 : 0;
                if (pct < 1) return null;
                return (
                  <div key={b.key} className="flex items-center justify-center text-white text-[9px] font-bold"
                    style={{ width: `${pct}%`, backgroundColor: b.color }} title={`${b.label}: ${fc(aging[b.key]?.amount || 0)} (${pct.toFixed(0)}%)`}>
                    {pct > 15 ? `${pct.toFixed(0)}%` : ''}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {AGING.map(b => <span key={b.key} className="flex items-center gap-1 text-[9px] text-muted-foreground">
                <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: b.color }} />{b.label}: {fc(aging[b.key]?.amount || 0)}
              </span>)}
            </div>
          </div>
        </>
      )}

      {tab === 'aging' && data && (
        <div className="space-y-2">
          {(data.top_overdue || []).map((inv, i) => (
            <div key={i} className="flex items-center gap-2 p-2 rounded-xl border hover:border-[#800000]/20 hover:bg-[#800000]/5 transition-all cursor-pointer" onClick={() => navigate('/invoices')}>
              <div className={`w-1.5 rounded-full self-stretch shrink-0 ${inv.days_overdue > 90 ? 'bg-red-800' : inv.days_overdue > 60 ? 'bg-red-500' : inv.days_overdue > 30 ? 'bg-orange-500' : 'bg-amber-400'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-800 truncate">{inv.account_name}</p>
                <p className="text-[10px] text-muted-foreground">{inv.invoice_number}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-bold text-red-700 font-mono">{fc(inv.amount)}</p>
                <p className={`text-[9px] font-bold px-1 py-0.5 rounded ${inv.days_overdue > 60 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{inv.days_overdue}d</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'by-rep' && data && (
        <HBar items={(data.by_salesperson || []).map(s => ({ label: s.name?.split(' ')[0] || s.name, value: s.overdue, suffix: `${s.collection_pct}% coll.` }))}
          maxValue={Math.max(...(data.by_salesperson || []).map(s => s.overdue), 1)}
          color="#dc2626" formatValue={v => formatCurrency(v, currency)} />
      )}
    </HealthCardShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CARD 2: Deal Velocity & Stalled Deals
// ═══════════════════════════════════════════════════════════════════════════════
export function DealVelocityCard({ filters = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const { formatCurrency, currency } = useCurrency();
  const navigate = useNavigate();
  const requestParams = buildHealthCardParams(filters);
  const requestParamsKey = useMemo(() => JSON.stringify(requestParams), [requestParams]);

  useEffect(() => {
    setLoading(true);
    crmAPI.getDealVelocityHealth(requestParams)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestParamsKey]);

  const s = data?.summary || {};
  const stall_pct = s.total_active > 0 ? round(s.stalled_count / s.total_active * 100, 1) : 0;
  function round(v, d) { return Math.round(v * Math.pow(10, d)) / Math.pow(10, d); }

  return (
    <HealthCardShell title="Deal Velocity & Stalled Deals" icon={BarChart2} iconColor="#f59e0b"
      tabs={[{id:'overview',label:'Overview'},{id:'stalled',label:'Stalled'},{id:'stages',label:'By Stage'}]}
      activeTab={tab} onTabChange={setTab} loading={loading} testId="deal-velocity-card" scopeFilters={filters}>

      {tab === 'overview' && data && (
        <>
          <div className="flex items-center gap-4 mb-3">
            <div className="relative shrink-0">
              <Ring pct={s.healthy_pct || 0} color={s.healthy_pct >= 70 ? '#10b981' : s.healthy_pct >= 50 ? '#f59e0b' : '#dc2626'} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-xl font-black ${s.healthy_pct >= 70 ? 'text-emerald-600' : s.healthy_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{s.healthy_pct}%</span>
                <span className="text-[9px] text-muted-foreground">healthy</span>
              </div>
            </div>
            <div className="flex-1">
              <KpiStrip items={[
                { label: 'Active', value: s.total_active, bg: '#f8fafc', border: '#e2e8f0', labelColor: '#64748b', valueColor: '#0f172a' },
                { label: 'Stalled', value: s.stalled_count, sub: `${stall_pct}%`, bg: '#fef2f2', border: '#fecaca', labelColor: '#991b1b', valueColor: '#dc2626' },
                { label: 'Healthy', value: s.healthy_count, bg: '#ecfdf5', border: '#bbf7d0', labelColor: '#065f46', valueColor: '#059669' },
              ]} />
              {s.stalled_count > 0 && (
                <div className="rounded-xl px-3 py-2 mt-2" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #dc2626 100%)' }}>
                  <p className="text-[9px] text-white/80">Stalled pipeline at risk</p>
                  <p className="text-sm font-black text-white font-mono">{formatCurrency(s.stalled_value, currency)}</p>
                </div>
              )}
            </div>
          </div>
          {/* Stage health bars */}
          <div className="rounded-xl bg-slate-50 p-2.5">
            <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Stall Rate by Stage</p>
            {(data.by_stage || []).slice(0, 5).map(s => (
              <div key={s.stage} className="flex items-center gap-2 mb-1.5 text-[10px]">
                <span className="truncate w-28 text-muted-foreground">{s.stage}</span>
                <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${s.stall_pct}%`, background: s.stall_pct > 70 ? '#dc2626' : s.stall_pct > 40 ? '#f59e0b' : '#10b981' }} />
                </div>
                <span className="font-semibold w-8 text-right">{s.count}</span>
                <span className="text-muted-foreground w-14 text-right">avg {s.avg_days}d</span>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'stalled' && data && (
        <div className="space-y-1.5">
          {(data.stalled_deals || []).map((d, i) => (
            <div key={i} className="flex items-start gap-2 p-2.5 rounded-xl border hover:border-[#800000]/20 cursor-pointer group transition-all"
              onClick={() => navigate('/opportunities')}>
              <div className={`w-1 rounded-full self-stretch shrink-0 ${d.days_stalled > 90 ? 'bg-red-600' : d.days_stalled > 60 ? 'bg-orange-500' : 'bg-amber-400'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-800 truncate">{d.name}</p>
                <p className="text-[10px] text-muted-foreground">{d.account_name} · {d.owner_name?.split(' ')[0]}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-bold text-amber-700 font-mono">{formatCurrency(d.value, currency)}</p>
                <p className="text-[9px] bg-amber-100 text-amber-800 px-1 py-0.5 rounded font-bold">{d.days_stalled}d stalled</p>
              </div>
            </div>
          ))}
          <button className="w-full text-xs text-amber-700 font-semibold py-1.5 hover:bg-amber-50 rounded-lg" onClick={() => navigate('/opportunities')}>
            View all stalled →
          </button>
        </div>
      )}

      {tab === 'stages' && data && (
        <div className="space-y-2">
          {(data.by_stage || []).map((s, i) => (
            <div key={i} className="rounded-xl bg-slate-50 p-2.5">
              <div className="flex justify-between text-xs mb-1">
                <span className="font-semibold">{s.stage}</span>
                <div className="flex gap-3 text-muted-foreground">
                  <span>{s.count} deals</span>
                  <span>avg {s.avg_days}d</span>
                  <span className={`font-bold ${s.stall_pct > 50 ? 'text-red-600' : 'text-amber-600'}`}>{s.stall_pct}% stalled</span>
                </div>
              </div>
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${s.stall_pct}%`, background: s.stall_pct > 50 ? '#dc2626' : '#f59e0b' }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </HealthCardShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CARD 3: Win / Loss Intelligence
// ═══════════════════════════════════════════════════════════════════════════════
export function WinLossIntelligenceCard({ filters = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const { formatCurrency, currency } = useCurrency();
  const requestParams = buildHealthCardParams(filters);
  const requestParamsKey = useMemo(() => JSON.stringify(requestParams), [requestParams]);

  useEffect(() => {
    setLoading(true);
    crmAPI.getWinLossIntelligence(requestParams)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestParamsKey]);

  const s = data?.summary || {};
  const trend = s.curr_win_rate - s.prev_win_rate;

  return (
    <HealthCardShell title="Win / Loss Intelligence" icon={Target} iconColor="#7c3aed"
      tabs={[{id:'overview',label:'Overview'},{id:'category',label:'Category'},{id:'trend',label:'Trend'}]}
      activeTab={tab} onTabChange={setTab} loading={loading} testId="win-loss-intelligence" scopeFilters={filters}>

      {tab === 'overview' && data && (
        <>
          <div className="flex items-center gap-4 mb-3">
            <div className="relative shrink-0">
              <Ring pct={s.win_rate || 0} color="#7c3aed" />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-black text-violet-700">{s.win_rate}%</span>
                <span className="text-[9px] text-muted-foreground">win rate</span>
              </div>
            </div>
            <div className="flex-1">
              <KpiStrip items={[
                { label: 'Won', value: s.won_count, sub: formatCurrency(s.won_value, currency), bg: '#ecfdf5', border: '#bbf7d0', labelColor: '#065f46', valueColor: '#059669' },
                { label: 'Lost', value: s.lost_count, sub: formatCurrency(s.lost_value, currency), bg: '#fef2f2', border: '#fecaca', labelColor: '#991b1b', valueColor: '#dc2626' },
              ]} />
              <div className="mt-2 rounded-xl p-2.5 bg-violet-50 border border-violet-100">
                <div className="flex justify-between text-xs">
                  <span className="text-violet-600">vs Last Year</span>
                  <span className={`font-bold flex items-center gap-1 ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {trend >= 0 ? '+' : ''}{trend.toFixed(1)}pp
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                  <span>{s.prev_year || 'Last year'}: {s.prev_win_rate}%</span>
                  <span>→</span>
                  <span className="font-bold">{s.curr_year}: {s.curr_win_rate}%</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {tab === 'category' && data && (
        <HBar
          items={(data.by_category || []).map(c => ({ label: (c.category || 'Other').split(' ').slice(-2).join(' '), value: c.won_value, suffix: `${c.win_rate}% wr` }))}
          maxValue={Math.max(...(data.by_category || []).map(c => c.won_value), 1)}
          color="#7c3aed" formatValue={v => formatCurrency(v, currency)} />
      )}

      {tab === 'trend' && data && (
        <div className="space-y-1.5">
          {(data.monthly_trend || []).slice(-8).map(m => {
            const total = m.won + m.lost;
            const wr = m.win_rate;
            return (
              <div key={m.month} className="flex items-center gap-2 text-[10px]">
                <span className="text-muted-foreground w-12 shrink-0">{m.month.slice(5)}</span>
                <div className="flex-1 flex h-4 rounded-lg overflow-hidden">
                  <div className="bg-emerald-400 flex items-center justify-center text-white text-[8px]" style={{ width: `${wr}%` }}>
                    {wr > 25 ? `${m.won}W` : ''}
                  </div>
                  <div className="bg-red-300 flex items-center justify-center text-white text-[8px]" style={{ width: `${100-wr}%` }}>
                    {(100-wr) > 25 ? `${m.lost}L` : ''}
                  </div>
                </div>
                <span className={`font-bold w-10 text-right ${wr >= 70 ? 'text-emerald-600' : wr >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{wr}%</span>
              </div>
            );
          })}
        </div>
      )}
    </HealthCardShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CARD 4: Activity Cadence Health
// ═══════════════════════════════════════════════════════════════════════════════
export function ActivityCadenceHealthCard({ filters = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const requestParams = buildHealthCardParams(filters);
  const requestParamsKey = useMemo(() => JSON.stringify(requestParams), [requestParams]);

  useEffect(() => {
    setLoading(true);
    crmAPI.getActivityCadenceHealth(requestParams)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestParamsKey]);

  const s = data?.summary || {};

  const AVATAR_COLORS = ['#800000','#2563eb','#059669','#7c3aed','#d97706','#0891b2','#db2777'];
  const avatarColor = (name = '') => AVATAR_COLORS[[...name].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];

  return (
    <HealthCardShell title="Activity Cadence Health" icon={Zap} iconColor="#0891b2"
      tabs={[{id:'overview',label:'Overview'},{id:'team',label:'By Person'},{id:'weekly',label:'Weekly'}]}
      activeTab={tab} onTabChange={setTab} loading={loading} testId="activity-cadence-health" scopeFilters={filters}>

      {tab === 'overview' && data && (
        <>
          <div className="flex items-center gap-4 mb-3">
            <div className="relative shrink-0">
              <Ring pct={s.on_target_pct || 0} color="#0891b2" />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-black text-cyan-700">{s.on_target_pct}%</span>
                <span className="text-[9px] text-muted-foreground">on target</span>
              </div>
            </div>
            <div className="flex-1">
              <KpiStrip items={[
                { label: 'On Target', value: s.on_target, sub: `≥${s.weekly_target}/wk`, bg: '#ecfdf5', border: '#bbf7d0', labelColor: '#065f46', valueColor: '#059669' },
                { label: 'At Risk', value: s.at_risk, bg: '#fffbeb', border: '#fcd34d', labelColor: '#92400e', valueColor: '#d97706' },
                { label: 'Inactive', value: s.inactive, bg: '#fef2f2', border: '#fecaca', labelColor: '#991b1b', valueColor: '#dc2626' },
              ]} />
            </div>
          </div>
          {/* Team weekly bar chart */}
          <div className="rounded-xl bg-slate-50 p-2.5">
            <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400 mb-2">Team Activity (last 8 weeks)</p>
            <div className="flex items-end gap-0.5 h-12">
              {(data.team_weekly || []).map((w, i) => {
                const max = Math.max(...(data.team_weekly || []).map(x => x.total), 1);
                const h = Math.max(2, Math.round(w.total / max * 44));
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${w.label}: ${w.total} activities`}>
                    <div className={`w-full rounded-sm ${w.is_current ? 'bg-cyan-500' : 'bg-cyan-200'}`} style={{ height: h }} />
                    {w.is_current && <span className="text-[7px] text-cyan-600 font-bold">NOW</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {tab === 'team' && data && (
        <div className="space-y-2">
          {(data.by_person || []).map((p, i) => (
            <div key={p.name} className="rounded-xl bg-slate-50 p-2.5" data-testid={`cadence-person-${i}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                  style={{ backgroundColor: avatarColor(p.name) }}>
                  {p.name[0]}
                </div>
                <span className="text-xs font-semibold flex-1">{p.name.split(' ').slice(0,2).join(' ')}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  p.status === 'on_target' ? 'bg-emerald-100 text-emerald-700' :
                  p.status === 'at_risk' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                }`}>
                  {p.current_week}/wk {p.trend === 'up' ? '↑' : p.trend === 'down' ? '↓' : '→'}
                </span>
              </div>
              <div className="flex gap-0.5 h-6">
                {(p.weekly_counts || []).map((c, wi) => {
                  const max = Math.max(...(p.weekly_counts || []), s.weekly_target || 5, 1);
                  const h = Math.max(2, Math.round(c / max * 22));
                  return (
                    <div key={wi} className="flex-1 flex items-end">
                      <div className={`w-full rounded-sm ${c >= (s.weekly_target || 5) ? 'bg-emerald-500' : c > 0 ? 'bg-amber-400' : 'bg-gray-200'}`} style={{ height: h }} />
                    </div>
                  );
                })}
              </div>
              <p className="text-[9px] text-muted-foreground mt-0.5">{p.avg_per_week}/wk avg · {p.weeks_on_target}/{data.by_person?.length || 8}w on target</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'weekly' && data && (
        <div className="space-y-1.5">
          {(data.team_weekly || []).map((w, i) => {
            const max = Math.max(...(data.team_weekly || []).map(x => x.total), 1);
            return (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className={`text-muted-foreground w-12 shrink-0 ${w.is_current ? 'font-bold text-cyan-600' : ''}`}>{w.label}</span>
                <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${w.is_current ? 'bg-cyan-500' : 'bg-cyan-200'}`}
                    style={{ width: `${Math.round(w.total / max * 100)}%` }} />
                </div>
                <span className={`font-bold w-6 text-right ${w.is_current ? 'text-cyan-700' : 'text-muted-foreground'}`}>{w.total}</span>
              </div>
            );
          })}
        </div>
      )}
    </HealthCardShell>
  );
}
